import { load } from 'cheerio'
import type { Cheerio, CheerioAPI } from 'cheerio'
import type { Element } from 'domhandler'
import { formFieldKeys, nameComponents } from '@messenger/consts'
import { nameArrangementSchema } from '@messenger/types'
import type { NameArrangement } from '@messenger/types'
import { formatCaseDetailValue } from './formatCaseDetailValue'
import { inkOf, isNamePartSpan, renderedTextOf } from './namePartSeparators'

/**
 * The name-of-deceased wrapper (`[data-case-detail="name_of_deceased"]`, one
 * child span per part) rebuilt to the case's arrangement, in the DOM
 * contract the Name Order plugin writes when its dialog is applied:
 *
 * - the part spans re-appended in the arrangement's order; a part the
 *   wrapper lacks gets a span cloned from an existing part span (the
 *   plugin's own approach), with its value written into its deepest span;
 * - a part the arrangement hides: `display: none` and `data-user-hidden`;
 *   a shown part loses the attribute, and is displayed when it has a
 *   value, hidden (as an empty part) when it has none;
 * - exactly one separator between consecutive visible parts — a styled
 *   space span, its style that of the previous part's innermost span, as
 *   the plugin writes it — and none at the edges.
 *
 * Whitespace and separators the wrapper held before are dropped, like the
 * plugin's rewrite drops them. The result is the same whether the wrapper
 * arrives fresh from an insert, from a saved order, or from this function's
 * own last pass, so the refresh can run it on every case change.
 *
 * Two wrappers are not rebuilt. The caller's no-arrangement path renders
 * them as it renders every name box of a case with no arrangement: a box
 * holding the name as text prints the flat name, and a box of part spans
 * prints its parts as its HTML marks them.
 *
 * - a wrapper with no part span among its children that holds the name as
 *   text, often inside spans carrying its font and size, which a rebuild
 *   would drop. Text counts where it renders, and so does an element showing
 *   the hair space the no-arrangement path writes while the case's flat name
 *   is empty, since the next name prints in that element; a wrapper holding
 *   nothing, whitespace, hair spaces as its own text, zero-width characters
 *   or `display: none` content is rebuilt.
 * - any wrapper, when the arrangement shows no part that has a value and
 *   the case has a flat name, the case in which a Lotus box prints the flat
 *   name (`resolveEditorCompositeNameParts`) and a rebuild prints nothing.
 */

type NamePart = (typeof nameComponents)[keyof typeof nameComponents]

/** The case column each part of the shared vocabulary prints from. */
export const NAME_PART_COLUMNS: Readonly<Record<NamePart, string>> = {
  [nameComponents.prefix]: formFieldKeys.prefix,
  [nameComponents.first]: formFieldKeys.firstName,
  [nameComponents.nickname]: formFieldKeys.nickName,
  [nameComponents.middle]: formFieldKeys.middleName,
  [nameComponents.maiden]: formFieldKeys.maidenName,
  [nameComponents.last]: formFieldKeys.lastName,
  [nameComponents.suffix]: formFieldKeys.suffix,
}

/** The part of the shared vocabulary each case column prints: the inverse of NAME_PART_COLUMNS. */
export const NAME_PART_OF_COLUMN: Readonly<Record<string, NamePart>> =
  Object.fromEntries(
    Object.entries(NAME_PART_COLUMNS).map(([part, column]) => [
      column,
      part as NamePart,
    ]),
  )

/** The case column holding the name as one string, the flat name. */
const FLAT_NAME_COLUMN = 'name_of_deceased'

/**
 * What an empty part holds, the hair space the insert writes; the
 * no-arrangement path writes the same into a name box for an empty flat name.
 */
const EMPTY_PART_TEXT = '\u200A'

/** The case's value in `column`, trimmed: '' when it has none. */
const valueOf = (
  caseDetails: Record<string, unknown> | null | undefined,
  column: string,
): string => {
  const raw = caseDetails?.[column]
  return typeof raw === 'string' ? raw.trim() : ''
}

/**
 * Whether the wrapper holds the name as text: a child renders visible text,
 * or a child element shows the hair space written for an empty flat name.
 */
const holdsNameText = (wrapper: Cheerio<Element>): boolean =>
  wrapper
    .contents()
    .toArray()
    .some((node) => {
      const text = inkOf(renderedTextOf(node))
      return (
        text.trim() !== '' ||
        (node.type === 'tag' && text.includes(EMPTY_PART_TEXT))
      )
    })

const DISPLAY_DECLARATION = /\s*display\s*:\s*[^;]*;?/gi

const setHidden = (span: Cheerio<Element>, hidden: boolean): void => {
  const kept = (span.attr('style') ?? '')
    .replace(DISPLAY_DECLARATION, '')
    .trim()
  const style = hidden
    ? `${kept}${kept === '' || kept.endsWith(';') ? '' : ';'} display: none;`.trim()
    : kept
  if (style === '') span.removeAttr('style')
  else span.attr('style', style)
}

/** The innermost span of a part span, the one that carries the text and the style. */
const innermost = (span: Cheerio<Element>): Cheerio<Element> => {
  let deepest = span
  while (deepest.children('span').length > 0)
    deepest = deepest.children('span').first()
  return deepest
}

const spaceSpan = (
  $: CheerioAPI,
  after: Cheerio<Element>,
): Cheerio<Element> => {
  const style = innermost(after).attr('style')
  const span = $('<span> </span>') as Cheerio<Element>
  if (style) span.attr('style', style)
  return span
}

/** A span for a part the wrapper lacks: a clone of an existing part span, else a bare one. */
const partSpan = (
  $: CheerioAPI,
  template: Cheerio<Element> | undefined,
  column: string,
  text: string,
): Cheerio<Element> => {
  const span = template
    ? template.clone()
    : ($(
        '<span class="mceNonEditable" contenteditable="false"></span>',
      ) as Cheerio<Element>)
  span.attr('data-case-detail', column)
  innermost(span).text(text)
  return span
}

/**
 * Rebuilds `wrapper` to `arrangement` and returns true. Returns false,
 * changing nothing, when the arrangement is not the case column's shape,
 * when it shows no part that has a value while the case has a flat name,
 * or when the wrapper has no part spans and holds the name as text.
 */
export const arrangeNameWrapper = (
  wrapper: Cheerio<Element>,
  arrangement: unknown,
  caseDetails: Record<string, unknown> | null | undefined,
): boolean => {
  const parsed = nameArrangementSchema.safeParse(arrangement)
  if (!parsed.success) return false
  const $: CheerioAPI = load('')
  const { order, hidden }: NameArrangement = parsed.data
  const hiddenParts = new Set<string>(hidden)

  // Every refusal comes before the first change to the wrapper.
  const showsValuedPart = order.some(
    (part) =>
      !hiddenParts.has(part) &&
      valueOf(caseDetails, NAME_PART_COLUMNS[part]) !== '',
  )
  if (!showsValuedPart && valueOf(caseDetails, FLAT_NAME_COLUMN) !== '')
    return false

  const existing = new Map<string, Cheerio<Element>>()
  wrapper.children('[data-case-detail]').each((_, el) => {
    const column = $(el).attr('data-case-detail') ?? ''
    if (isNamePartSpan(el) && !existing.has(column)) existing.set(column, $(el))
  })
  if (existing.size === 0 && holdsNameText(wrapper)) return false
  const template = existing.values().next().value as
    | Cheerio<Element>
    | undefined

  const children: Cheerio<Element>[] = []
  let previousVisible: Cheerio<Element> | null = null
  for (const part of order) {
    const column = NAME_PART_COLUMNS[part]
    const value = valueOf(caseDetails, column)
    const hasValue = value !== ''
    const visible = hasValue && !hiddenParts.has(part)

    const span =
      existing.get(column) ??
      partSpan(
        $,
        template,
        column,
        hasValue
          ? formatCaseDetailValue({ value, path: column })
          : EMPTY_PART_TEXT,
      )
    setHidden(span, !visible)
    if (hiddenParts.has(part)) span.attr('data-user-hidden', 'true')
    else span.removeAttr('data-user-hidden')

    if (visible && previousVisible !== null)
      children.push(spaceSpan($, previousVisible))
    children.push(span)
    if (visible) previousVisible = span
  }

  wrapper.empty()
  for (const child of children) wrapper.append(child)
  return true
}

export default arrangeNameWrapper
